<template>
    <div>
        <div class="row">
            <div v-bind:class="responsiveClasses.emailWrapper">
                <pc-input
                    v-model="newOwnerEmail"
                    v-bind:external-native-handlers="{ keyup: onChangeProjectOwnerKeyUp }"
                    v-bind:validator="changeOwnerValidator"
                    v-bind:class="responsiveClasses.emailInput"
                    v-bind:show-validation-errors="!wideView"
                    immediate-validation
                    v-bind:label="changeOwnerEmailLabel"
                    name="NewOwnerEmail"
                    inputmode="email"
                    class="flex-grow-1"
                    v-on:validated="onEmailValidated" />
            </div>
            <div
                v-bind:class="responsiveClasses.buttonWrapper"
                class="d-flex align-items-end justify-content-end">
                <pc-button
                    v-if="isAdmin"
                    class="me-2"
                    v-on:click="$emit('cancel')">
                    {{ cancelText }}
                </pc-button>
                <pc-loader
                    v-bind:loading="changeOwnerLoading"
                    small>
                    <pc-button
                        primary
                        v-bind:disabled="disableChange"
                        v-on:click="changeProjectOwner">
                        {{ isAdmin ? saveText : changeOwnerButton }}
                    </pc-button>
                </pc-loader>
            </div>
            <validation-error-helper
                v-if="wideView && hasValidationError"
                v-bind:validation-error="validationError"
                class="mt-0" />
        </div>
        <pc-alert
            v-model:shown="showAlert"
            v-bind:duration="alertDuration"
            v-bind:severity="alertSeverity"
            v-bind:message="alertMessage"
            v-bind:class="responsiveClasses.alert"
            class="mt-3 mb-0" />
    </div>
</template>
<script>
    import { ALERT_SEVERITY, ALERT_DURATION } from "Util/alerts";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { defaultAlertDuration, WIDE_BP } from "./_sharedCapsuleBase";
    import { isAtLeast } from "Responsive/manager";
    import validationErrorHelper from "Validation/_validation-error-helper.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/changeOwnerControl.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        emailWrapper: {
            col: { xs: 12, [WIDE_BP.name]: 6 }
        },
        emailInput: {
            mb: { xs: 2, [WIDE_BP.name]: 0 }
        },
        buttonWrapper: {
            col: { xs: 12, [WIDE_BP.name]: "" }
        },
        alert: {
            col: { xs: 12, [WIDE_BP.name]: "" }
        }
    };

    export default {
        components: {
            validationErrorHelper
        },
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            programId: { type: String, required: true },
            projectId: { type: String, required: true },
            projectOwner: { type: Object, required: true },
            controller: { type: Object, required: true },
            isAdmin: { type: Boolean, required: false, default: false }
        },
        emits: ["cancel", "change"],
        data: function () {
            return {
                newOwnerEmail: null,

                changeOwnerLoading: false,
                changeOwnerValidator: getValidator(this.$app, VALIDATOR_TYPES.email),
                emailValidationResult: null,

                showAlert: false,
                alertSeverity: ALERT_SEVERITY.success,
                alertDuration: defaultAlertDuration,
                alertMessage: null
            };
        },
        computed: {
            ...mapI18n(KEYS, NAMESPACE),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE),
            changeOwnerWarning() {
                const alertText = this.$app.res({
                    namespace: NAMESPACE,
                    key: KEYS.changeOwnerConfirmationText,
                    values: {
                        projectOwnerEmail: this.projectOwner.Email,
                        newOwnerEmail: this.newOwnerEmail
                    }
                });
                return alertText;
            },
            disableChange() {
                return this.emailValidationResult && this.emailValidationResult.hasError;
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            hasValidationError() {
                return this.emailValidationResult && this.emailValidationResult.hasError;
            },
            validationError() {
                return this.emailValidationResult && this.emailValidationResult.error;
            },
            wideView() {
                return isAtLeast(this.viewport, WIDE_BP);
            }
        },
        methods: {
            async changeProjectOwner() {
                if (this.disableChange) {
                    return;
                }

                if (this.changeOwnerLoading) {
                    return;
                }

                this.showAlert = false;
                if (!this.newOwnerEmail) {
                    this._showAlert(this.newOwnerEmailPrompt, ALERT_SEVERITY.info, ALERT_DURATION.long);
                    return;
                }

                if (this.newOwnerEmail === this.projectOwner.Email) {
                    const alertText = this.$app.res({
                        namespace: NAMESPACE,
                        key: KEYS.alreadyOwnerText,
                        values: { newOwnerEmail: this.newOwnerEmail }
                    });
                    this._showAlert(alertText, ALERT_SEVERITY.info, ALERT_DURATION.long);
                    return;
                }

                const cancelResult = "CANCEL";
                const okResult = "OK";
                const dialogResult = await this.$app.showDialog({
                    title: this.changeOwnerDialogTitle,
                    message: this.changeOwnerWarning,
                    buttons: [
                        {
                            text: this.cancelText,
                            result: cancelResult
                        },
                        {
                            text: this.okText,
                            primary: true,
                            result: okResult
                        }
                    ]
                });

                if (dialogResult === cancelResult) {
                    return;
                }

                this.changeOwnerLoading = true;
                try {
                    const result = await this.controller.ChangeOwner({
                        programPublicId: this.programId,
                        projectPublicId: this.projectId,
                        email: this.newOwnerEmail
                    });
                    if (result.status === "success") {
                        if (result.Data.ProjectOwnerModel != null) {
                            this.$emit("change", {
                                owner: result.Data.ProjectOwnerModel,
                                ownerRole: result.Data.ProjectOwnerRole
                            });
                        } else {
                            this._showAlert(result.Data.ErrorMessage, ALERT_SEVERITY.error, ALERT_DURATION.indefinite);
                        }
                    }
                } finally {
                    this.changeOwnerLoading = false;
                }
            },
            onChangeProjectOwnerKeyUp(event) {
                if (event.key == "Enter" || event.keyCode == 13) {
                    this.changeProjectOwner();
                }
            },
            onEmailValidated(result) {
                this.emailValidationResult = result;
            },
            _showAlert(message, severity, duration) {
                this.alertMessage = message;
                this.alertSeverity = severity;
                this.alertDuration = duration;
                this.showAlert = true;
            }
        }
    };
</script>
